import { Invoice, InvoiceStatus, StatementPaymentSummary } from '../../clients/quipli-service/invoice/invoice';
import { useCallback, useContext, useEffect, useState } from 'react';
import { usePaymentApiV2 } from '../../clients/quipli-service/payment/payment-api-v2';
import { toast } from '../../util/toasts';
import { FiltersModal } from '../../components/modal/filters-modal';
import { Typography } from '../../components/typography/typography';
import Icon from '../../components/icons/icon';
import { convertToMoney, dineroSum, displayMoneyFormat } from '../../util/util';
import { PaymentMethodSelectorV2 } from './payment-method-selector-v2';
import { PaymentMethodRequestV2 } from '../../clients/quipli-service/payment/payment-v2';
import { Payment, PaymentMethodType } from '../../clients/quipli-service/payment/payment';
import { useAPI } from '../../clients/quipli-service/quipli-service-client';
import { Spinner } from '../../components/spinner/spinner';
import { CashPaymentInput } from './cash-payment-input';
import { User } from '../../clients/quipli-service/user/user';
import { useInvoiceApiV1 } from '../../clients/quipli-service/invoice/invoice-api-v1';
import _, { debounce } from 'lodash';
import { Currency } from 'dinero.js';
import { TenantContext } from '../../context/tenant-context';
import { Checkbox } from '../../components/checkbox/checkbox';
import { PaymentContextProvider } from '../../context/payment-context';
import { DateTime } from 'luxon';
import { DatePicker } from '../../components/date-picker/date-picker';
import { LocationSelect } from '../location/select/location-select';
import { Location } from '../../clients/quipli-service/location/location';
import { MoneyInput } from '../../components/form/money-input';

export const PaymentModal = ({
  isOpen,
  close,
  currency,
  user,
  balance,
  invoice,
  statementPayment = false,
  locations,
}: {
  isOpen: boolean;
  close: (payments?: Payment[]) => void;
  currency: string;
  user: User;
  balance?: number;
  invoice?: Invoice;
  statementPayment?: boolean;
  locations?: Location[];
}) => {
  const { createPayment } = usePaymentApiV2();
  const { getStatementPaymentSummary, getInvoiceById } = useInvoiceApiV1();
  const { getMultipleConfigurations, getOrderById, listLocations } = useAPI();

  const tenant = useContext(TenantContext);
  const [paymentMethod, setPaymentMethod] = useState<PaymentMethodRequestV2 | undefined>();
  const [paymentMethodType, setPaymentMethodType] = useState<PaymentMethodType | undefined>();
  const [amount, setAmount] = useState<number>(invoice?.balance ?? balance ?? 0);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isCapturing, setIsCapturing] = useState<boolean>(false);
  const [facilitate, setFacilitate] = useState<boolean>(false);
  const remainingBalance = invoice ? invoice?.balance - amount : 0;
  const formattedAmount = displayMoneyFormat(amount, currency);
  const [payment, setPayment] = useState<Payment>();
  const [locationId, setLocationId] = useState<number | undefined>(invoice?.locationId);
  const [statementSummary, setStatementSummary] = useState<StatementPaymentSummary[]>([]);
  const [error, setError] = useState<string>();
  const [selectedInvoices, setSelectedInvoices] = useState<string[]>([]);
  const [paymentDate, setPaymentDate] = useState<DateTime | undefined>(DateTime.now());
  const [globalTimezone, setGlobalTimezone] = useState<string | undefined>();

  useEffect(() => {
    getMultipleConfigurations(['CONF_TIMEZONE']).then((config) => {
      setGlobalTimezone(config.find((_) => _.variable == 'CONF_TIMEZONE')?.value);
    });
  }, []);

  const getStatementSummary = useCallback(
    debounce(function () {
      getStatementPaymentSummary({
        amount: amount > 0 ? amount : 0,
        userId: user.id,
        selectedInvoices: selectedInvoices,
      })
        .then((summary) => {
          setError(undefined);
          setStatementSummary(summary);
          if (!locationId) {
            const firstInvoice = summary.find((_) => _)?.invoiceId;
            if (firstInvoice) {
              getInvoiceById(firstInvoice).then((invoice) => {
                setLocationId(invoice.locationId);
              });
            } else {
              listLocations(true).then((locations) => {
                setLocationId(locations[0].id);
              });
            }
          }
        })
        .catch((e: any) => {
          setError(e.message);
        });
    }, 500),
    [amount],
  );

  useEffect(() => {
    if (statementPayment) {
      getStatementSummary();
    }
  }, [amount]);

  useEffect(() => {
    const newAmount = dineroSum(
      statementSummary
        .filter((_) => selectedInvoices.includes(_.invoiceId))
        .map((_) => convertToMoney(_.due, currency as Currency)),
      currency as Currency,
    ).toUnit();
    selectedInvoices.length > 0 && setAmount(newAmount);
  }, [selectedInvoices]);

  useEffect(() => {
    if (invoice?.type && invoice.type === 'Order' && !paymentMethod) {
      getOrderById(parseInt(invoice.typeId)).then((order) => {
        let pm: PaymentMethodRequestV2 | undefined = undefined;
        switch (order.paymentMethod.type) {
          case PaymentMethodType.Stripe:
            pm = {
              type: PaymentMethodType.Stripe,
              cardId: order.paymentMethod.card?.id,
              method: order.paymentMethod.method,
            };
            break;
          case PaymentMethodType.Credit:
          case PaymentMethodType.OnAccount:
          case PaymentMethodType.None:
            break;
          default:
            pm = order.paymentMethod;
        }
        setPaymentMethod(pm);
        setIsLoading(false);
      });
    } else {
      setIsLoading(false);
    }
  }, [invoice, paymentMethod]);

  async function postPayment(payments?: Payment[]) {
    setFacilitate(false);
    if (payments) {
      const result = payments[0];
      if (result.status === 'failed') {
        try {
          const stripeResponse = JSON.parse(result.gatewayResponse || '{}');
          if (stripeResponse?.last_payment_error?.message) {
            toast(`Error: ${stripeResponse?.last_payment_error?.message}`, 'error');
          } else {
            toast('Error on payment', 'error');
          }
        } catch (ex: any) {
          toast('Error on payment', 'error');
        }
      } else if (result.gatewayStatus === 'requires_action') {
        toast('Payment requires authentication', 'error');
      } else {
        toast('Payment Successful');
      }
      close([result]);
    }
  }

  async function doPayment() {
    setIsCapturing(true);
    try {
      if (!paymentMethod) {
        toast('Please select a payment method', 'error');
        return;
      }

      const result = await createPayment({
        invoiceIds: [...(invoice?.id ? [invoice.id] : selectedInvoices)],
        amount: amount,
        paymentMethod,
        userId: user.id,
        allowOverpayment: statementPayment,
        paymentDate,
        locationId,
      });

      if (paymentMethod.type === PaymentMethodType.Stripe && paymentMethod.terminalId) {
        setFacilitate(true);
        setPayment(result);
      } else {
        if (result.status === 'failed') {
          try {
            const stripeResponse = JSON.parse(result.gatewayResponse || '{}');
            if (stripeResponse?.last_payment_error?.message) {
              toast(`Error: ${stripeResponse?.last_payment_error?.message}`, 'error');
            } else {
              toast('Error on payment', 'error');
            }
          } catch (ex: any) {
            toast('Error on payment', 'error');
          }
        } else if (result.gatewayStatus === 'requires_action') {
          toast('Payment requires authentication', 'error');
        } else {
          toast('Payment Successful');
        }

        close([result]);
      }
    } catch (ex: any) {
      toast(ex.message, 'error');
    }
    setIsCapturing(false);
  }

  const onPaymentDateChange = (newPaymentDate: DateTime | undefined) => {
    // Since payments can be location agnostic, we'll set the payment time to noon of the global timezone'
    if (newPaymentDate && globalTimezone) {
      newPaymentDate = newPaymentDate.setZone(globalTimezone, { keepLocalTime: true });
      newPaymentDate = newPaymentDate.set({ hour: 12, minute: 0, second: 0, millisecond: 0 });
    }
    setPaymentDate(newPaymentDate);
  };

  return (
    <FiltersModal
      isOpen={isOpen}
      title={invoice ? `Invoice #${invoice.invoiceNumber}` : `Make a statement payment`}
      confirmText={`Pay ${formattedAmount}`}
      cancelText="Cancel"
      allowConfirm={
        !isCapturing &&
        !error &&
        (!!invoice || statementPayment) &&
        !!paymentMethod &&
        amount > 0 &&
        (paymentMethod.type == PaymentMethodType.Credit ? !!paymentMethod.paymentId : true)
      }
      allowCancel={!isCapturing}
      showConfirmButtonFirst={false}
      onConfirm={async () => {
        await doPayment();
      }}
      onCancel={() => {
        close();
      }}
    >
      {invoice && (
        <div className="min-w-full mb-[24px] border border-solid border-[var(--grey-200)] rounded-lg flex flex-col gap-2">
          <div className="m-[8px] mb-[0] flex items-center justify-between">
            <Typography type="text-sm" weight="regular" className="text-[var(--grey-800)]">
              Due Date
            </Typography>
            <Typography type="text-sm" weight="regular" className="text-[var(--grey-800)]">
              {invoice.dueDate?.toFormat('LLL-dd t') || 'N/A'}
            </Typography>
          </div>
          <div className="m-[8px] flex items-center justify-between">
            <Typography type="text-sm" weight="medium" className="text-[var(--grey-800)]">
              Balance Due
            </Typography>
            <Typography type="text-sm" weight="medium" className="text-[var(--grey-800)]">
              {invoice.status != InvoiceStatus.Void ? displayMoneyFormat(invoice.balance, currency) : '-'}
            </Typography>
          </div>
        </div>
      )}

      {statementPayment && statementSummary.length > 0 && (
        <div className={'pb-10 gap-2 flex flex-column overflow-y-hidden max-h-[30%]'}>
          <Typography type={'text-lg'} weight={'semi-bold'}>
            Invoices
          </Typography>
          <div className={'flex flex-row gap-2 m-1'}>
            <Checkbox
              isChecked={selectedInvoices.length === statementSummary.length}
              onChange={(isChecked) => {
                if (isChecked) {
                  setSelectedInvoices(statementSummary.map((_) => _.invoiceId));
                } else {
                  setSelectedInvoices([]);
                }
              }}
            ></Checkbox>
            <Typography type={'text-sm'}>Apply to all invoices</Typography>
          </div>
          <div className={'rounded bg-gray-100  max-h-[30%] gap-2 p-2 overflow-y-scroll h-20'}>
            {statementSummary.map((i) => (
              <div className={'flex flex-row gap-2 mt-1'}>
                <Checkbox
                  isChecked={selectedInvoices.includes(i.invoiceId)}
                  onChange={(isChecked) => {
                    if (isChecked) {
                      setSelectedInvoices([...selectedInvoices, i.invoiceId]);
                    } else {
                      setSelectedInvoices(selectedInvoices.filter((id) => id !== i.invoiceId));
                    }
                  }}
                ></Checkbox>
                <Typography type={'text-sm'} key={i.invoiceId}>
                  {i.invoiceNumber} - {convertToMoney(i.amount, tenant.configuration!.currency! as Currency).toFormat()}{' '}
                  - {'Due: '} {convertToMoney(i.due, tenant.configuration!.currency! as Currency).toFormat()}
                </Typography>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="min-w-full flex flex-col gap-2 items-stretch">
        <Typography type="text-xl">Make Payment</Typography>
        {!invoice &&
          locations &&
          locations.length > 1 &&
          (!selectedInvoices.length || (statementSummary && _.sum(statementSummary.map((_) => _.amount)) < amount)) && (
            <div className="flex justify-between flex-col">
              {statementSummary && _.sum(statementSummary.map((_) => _.amount)) < amount && (
                <Typography type={'text-sm'} weight={'regular'} className={'text-[var(--error-500)]'}>
                  Select location to apply remainder balance
                </Typography>
              )}

              <div className="flex justify-between items-center">
                <Typography type={'text-md'}>Payment Location</Typography>
                <span className="flex-[0_1_62%] flex items-center">
                  <LocationSelect
                    orderLocation={locations.find((_) => _.id == locationId)}
                    setLocation={(location) => setLocationId(location?.id)}
                    dataCy="location-select"
                    dropdownProps={{ menuPortalTarget: document.body }}
                    clearable={false}
                    restoreLast={false}
                  />
                </span>
              </div>
            </div>
          )}
        {paymentMethod?.type === PaymentMethodType.Cash && paymentMethodType === paymentMethod?.type ? (
          <CashPaymentInput
            setAmount={setAmount}
            balanceDue={
              invoice?.balance ??
              (statementSummary &&
              selectedInvoices.length &&
              _.sum(statementSummary.filter((_) => selectedInvoices.includes(_.invoiceId)).map((_) => _.due)) > 0
                ? _.sum(statementSummary.filter((_) => selectedInvoices.includes(_.invoiceId)).map((_) => _.due))
                : 0)
            }
            setPaymentDate={onPaymentDateChange}
          />
        ) : (
          <>
            {paymentMethodType !== undefined &&
              paymentMethodType !== PaymentMethodType.Stripe &&
              paymentMethodType !== PaymentMethodType.Statement && (
                <div className="flex justify-between items-center">
                  <span>Payment Date</span>
                  <span className="flex items-center">
                    <DatePicker
                      value={paymentDate}
                      onChange={onPaymentDateChange}
                      placeholder="Payment Date"
                      alignDropdownRight
                      clearable
                    />
                  </span>
                </div>
              )}
            <div className="flex justify-between items-center">
              <span className="mr-8">Amount</span>
              <MoneyInput value={amount} setValue={setAmount} />
            </div>
          </>
        )}
        {remainingBalance > 0 && (
          <div className="bg-[var(--error-50)] p-[16px] border border-solid border-[var(--error-300)] rounded-lg flex-[0_1_100%]">
            <Typography
              type="text-sm"
              weight="regular"
              className="text-[var(--error-500)] flex items-center justify-start gap-2"
            >
              <Icon icon="InfoCircle" stroke="var(--error-500)" size="large" />
              By paying {formattedAmount} renter still owes {displayMoneyFormat(remainingBalance, currency)}
            </Typography>
          </div>
        )}
        {error && (
          <Typography type="text-sm" weight="regular" className="text-[var(--error-500)]">
            {error}
          </Typography>
        )}

        {!isLoading && locationId ? (
          <PaymentContextProvider>
            <PaymentMethodSelectorV2
              user={user}
              balance={amount}
              setAmount={setAmount}
              allowedPaymentMethods={[
                PaymentMethodType.Stripe,
                PaymentMethodType.Cash,
                PaymentMethodType.Check,
                PaymentMethodType.Other,
                PaymentMethodType.Custom,
                ...(!statementPayment ? [PaymentMethodType.Statement, PaymentMethodType.Credit] : []),
              ]}
              paymentMethod={paymentMethod}
              setPaymentMethod={(pm) =>
                [
                  PaymentMethodType.Stripe,
                  PaymentMethodType.Cash,
                  PaymentMethodType.Check,
                  PaymentMethodType.Other,
                  PaymentMethodType.Custom,
                  ...(!statementPayment ? [PaymentMethodType.Statement, PaymentMethodType.Credit] : []),
                ].indexOf(pm.type) >= 0 && setPaymentMethod(pm as PaymentMethodRequestV2)
              }
              setPaymentMethodType={setPaymentMethodType}
              locationId={invoice?.locationId ?? locationId}
              payments={[...(payment ? [payment] : [])]}
              facilitate={facilitate}
              onComplete={postPayment}
            />
          </PaymentContextProvider>
        ) : (
          <Spinner />
        )}
      </div>
    </FiltersModal>
  );
};
